import { useFormContext } from 'react-hook-form';
import { Button } from 'shared-ui';

import { ReactComponent as EditIcon } from 'assets/icons/permission-key.svg';
import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { ActionModal } from 'ui/actionModal/ActionModal';
import { PermissionEnum } from 'api/permissions/permissions.enum';
import { Form } from 'ui/form/Form';
import { useAppSelectOptions } from 'hooks/useAppSelectOptions/useAppSelectOptions';
import { usePermissions } from 'hooks/usePermissions/usePermissions';
import * as Styled from '../GroupTerminalsPermissionsMultiActions.styles';

import {
  EditGroupTerminalsPermissionsFormBody,
  EditGroupTerminalsPermissionsProps,
} from './EditGroupTerminalsPermissions.types';

export const EditGroupTerminalsPermissions = ({
  isModalOpen,
  onSubmit,
  isLoading,
  onModalClose,
  onModalOpen,
  checkedPermissions,
}: EditGroupTerminalsPermissionsProps) => {
  const { formatMessage } = useLocale();
  const { perms } = usePermissions();

  const {
    handleSubmit,
    control,
    formState: { errors },
    trigger,
    setValue,
  } = useFormContext<EditGroupTerminalsPermissionsFormBody>();

  const { permissionOptions } = useAppSelectOptions();

  return (
    <>
      <Button
        variant="outlined"
        color="primary"
        size="medium"
        startIcon={<EditIcon style={{ opacity: !perms?.UPR_GRUPOWE?.edit ? 0.4 : 1 }} />}
        onClick={onModalOpen}
        disabled={!perms?.UPR_GRUPOWE?.edit}
      >
        {formatMessage({ id: AppMessages['group.reader.permissions.multiAction.edit.button'] })}
      </Button>

      <ActionModal
        header={formatMessage({ id: AppMessages['group.reader.permissions.multiAction.edit.title'] })}
        open={isModalOpen}
        onClose={onModalClose}
        size="medium"
        onSubmit={handleSubmit(onSubmit)}
        isLoading={isLoading}
        submitButtonDisabled={isLoading || !!Object.keys(errors).length}
        submitButtonText={formatMessage({ id: AppMessages['group.reader.permissions.multiAction.edit.submit'] })}
      >
        <ActionModal.Subtitle>
          {formatMessage({ id: AppMessages['group.reader.permissions.multiAction.edit.subtitle'] })}{' '}
          {checkedPermissions.length}
        </ActionModal.Subtitle>
        <Styled.FormWrapper>
          <Form.ControlledSelect
            name="rejestrujOdbicie"
            control={control}
            isError={!!errors.rejestrujRcp?.message}
            onChange={(e) => {
              setValue('rejestrujOdbicie', e.target.value as PermissionEnum, { shouldDirty: true });
              errors.rejestrujRcp?.message && trigger('rejestrujRcp');
            }}
            label={formatMessage({ id: AppMessages['group.reader.permissions.field.registerRecord.label'] })}
            data-testid="odbicie-select"
            options={permissionOptions}
          />

          <Form.ControlledSelect
            name="otwierajZamek"
            control={control}
            isError={!!errors.rejestrujRcp?.message}
            onChange={(e) => {
              setValue('otwierajZamek', e.target.value as PermissionEnum, { shouldDirty: true });
              errors.rejestrujRcp?.message && trigger('rejestrujRcp');
            }}
            label={formatMessage({ id: AppMessages['group.reader.permissions.field.openLock.label'] })}
            data-testid="zamek-select"
            options={permissionOptions}
          />

          <Form.ControlledSelect
            name="rejestrujRcp"
            control={control}
            onChange={(e) => setValue('rejestrujRcp', e.target.value as PermissionEnum, { shouldDirty: true })}
            withHelperText
            errorMessage={errors.rejestrujRcp?.message}
            isError={!!errors.rejestrujRcp?.message}
            label={formatMessage({ id: AppMessages['group.reader.permissions.field.registerRcp.label'] })}
            data-testid="rcp-select"
            options={permissionOptions}
          />
        </Styled.FormWrapper>
      </ActionModal>
    </>
  );
};
